import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import Select from 'react-select';
import { NIVEAUX_SANS_DOMAINE, getCountries, getNigerOption } from './constants';

const DiplomeModal = ({
    isOpen,
    isEditMode,
    formData,
    errors,
    typesDiplome,
    niveauxEtude,
    domainesFormation,
    showOtherType,
    onClose,
    onSubmit,
    onInputChange,
    onCountryChange
}) => {
    const countries = useMemo(() => getCountries(), []);
    const nigerOption = useMemo(() => getNigerOption(countries), [countries]);

    const showDomaine = useMemo(() => {
        if (!formData.niveau_etude) return false;
        const niveau = niveauxEtude.find(n => String(n.id) === String(formData.niveau_etude))?.niveau || '';
        return !NIVEAUX_SANS_DOMAINE.some(n => niveau.toLowerCase().includes(n.toLowerCase()));
    }, [formData.niveau_etude, niveauxEtude]);

    const handleNiveauChange = (e) => {
        onInputChange(e);
        // Reset domaine when niveau changes
        const { name, value } = e.target;
        if (name === 'niveau_etude') {
            const event = { target: { name: 'domaine_formation', value: '' } };
            onInputChange(event);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-gray-800">
                        {isEditMode ? "Modifier le diplôme" : "Ajouter un diplôme"}
                    </h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>
                <form onSubmit={onSubmit} className="p-6 space-y-6">
                    {/* Type de diplôme */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Diplôme <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="diplome"
                            value={formData.diplome}
                            onChange={onInputChange}
                            className={`w-full px-4 py-2 border ${errors.diplome ? 'border-red-500' : 'border-gray-300'} rounded-lg`}
                        >
                            <option value="">Sélectionnez un diplôme</option>
                            {typesDiplome.map(type => (
                                <option key={type.id} value={type.id}>{type.nom}</option>
                            ))}
                            <option value="autre">Autre</option>
                        </select>
                        {errors.diplome && <p className="mt-1 text-sm text-red-600">{errors.diplome}</p>}
                        
                        {showOtherType && (
                            <div className="mt-2">
                                <input
                                    type="text"
                                    name="autre"
                                    value={formData.autre || ''}
                                    onChange={onInputChange}
                                    placeholder="Précisez le type de diplôme"
                                    className={`w-full px-4 py-2 border ${errors.autre ? 'border-red-500' : 'border-gray-300'} rounded-lg`}
                                />
                                {errors.autre && <p className="mt-1 text-sm text-red-600">{errors.autre}</p>}
                            </div>
                        )}
                    </div>

                    {/* Niveau d'études */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Niveau d'études <span className="text-red-500">*</span>
                        </label>
                        <select
                            name="niveau_etude"
                            value={formData.niveau_etude}
                            onChange={handleNiveauChange}
                            className={`w-full px-4 py-2 border ${errors.niveau_etude ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                        >
                            <option value="">Sélectionnez un niveau d'études</option>
                            {niveauxEtude.map(niveau => (
                                <option key={niveau.id} value={niveau.id}>{niveau.niveau}</option>
                            ))}
                        </select>
                        {errors.niveau_etude && <p className="mt-1 text-sm text-red-600">{errors.niveau_etude}</p>}
                    </div>

                    {/* Domaine de formation (conditionnel) */}
                    {showDomaine && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Domaine de formation <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="domaine_formation"
                                value={formData.domaine_formation}
                                onChange={onInputChange}
                                className={`w-full px-4 py-2 border ${errors.domaine_formation ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                            >
                                <option value="">Sélectionnez un domaine</option>
                                {domainesFormation.map(domaine => (
                                    <option key={domaine.id} value={domaine.id}>{domaine.nom}</option>
                                ))}
                            </select>
                            {errors.domaine_formation && <p className="mt-1 text-sm text-red-600">{errors.domaine_formation}</p>}
                        </div>
                    )}

                    {/* Établissement */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Établissement <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="etablissement"
                            value={formData.etablissement}
                            onChange={onInputChange}
                            className={`w-full px-4 py-2 border ${errors.etablissement ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                            placeholder="Ex: Université de Paris"
                        />
                        {errors.etablissement && <p className="mt-1 text-sm text-red-600">{errors.etablissement}</p>}
                    </div>

                    {/* Date d'obtention */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date d'obtention <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            name="date_obtention"
                            value={formData.date_obtention}
                            onChange={onInputChange}
                            className={`w-full px-4 py-2 border ${errors.date_obtention ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500`}
                        />
                        {errors.date_obtention && <p className="mt-1 text-sm text-red-600">{errors.date_obtention}</p>}
                    </div>

                    {/* Pays */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Pays <span className="text-red-500">*</span>
                        </label>
                        <Select
                            options={countries}
                            value={countries.find(country => country.label === formData.pays) || nigerOption}
                            onChange={onCountryChange}
                            className={errors.pays ? 'border-red-500' : ''}
                            placeholder="Sélectionnez un pays"
                        />
                        {errors.pays && <p className="mt-1 text-sm text-red-600">{errors.pays}</p>}
                    </div>

                    {/* Boutons */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                        >
                            Annuler
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
                        >
                            {isEditMode ? "Modifier" : "Ajouter"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default DiplomeModal;
